import { Audio, VolumeDown, VolumeMute, VolumeUp } from '@tunasong/icons'
import {  Typography, isMobileDevice, Grid, Slider} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useId } from 'react'
import { AudioController } from './engine/audio-controller.js'
import { gainAriaValueText, gainToPosition, positionToGain } from './lib/fader-taper.js'
import { Meter } from './meter.js'
import styles from './volume.module.css'

export interface VolumeProps {
  title?: string
  controller: AudioController
  onChange?(value: number): void
  onClose?(): void
}

export const Volume: FC<VolumeProps> = props => {
  const { controller, onChange, onClose, title = 'Volume' } = props
  const titleId = useId()

  /** The slider is the fader's position; the gain set and reported goes through the dB taper (GH #2897) */
  const handleVolume = (ev: unknown, position: number | number[]) => {
    if (typeof position !== 'number') {
      return
    }

    const gain = positionToGain(position)
    controller.gain = gain
    if (onChange) {
      onChange(gain)
    }
  }

  const handleMute = () => {
    controller.muted = !controller.muted
  }

  const handleMonitor = (val: boolean) => () => {
    // eslint-disable-next-line react-compiler/react-compiler
    controller.monitor = val
    if (!isMobileDevice()) {
      controller.enableDSPFeatures(val)
    }
  }

  const handleMax = (ev: unknown) => handleVolume(ev, 1)

  return (
    <div className={styles.container}>
      <Typography id={titleId} gutterBottom variant="caption">
        {title}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 1 }}>
          {controller.type === 'mic' ? (
            controller.monitor ? (
              <Audio color="var(--tuna-palette-warning-main)" onClick={handleMonitor(false)} />
            ) : (
              <Audio onClick={handleMonitor(true)} />
            )
          ) : null}
        </Grid>

        <Grid size={{ xs: 1 }}>
          {controller.muted ? <VolumeMute color="var(--tuna-palette-error-main)" onClick={handleMute} /> : <VolumeDown onClick={handleMute} />}
        </Grid>
        <Grid className={styles.sliderGrid} size={{ xs: 9 }}>
          <Slider
            className={styles.slider}
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : 'Volume'}
            disabled={controller.muted}
            value={gainToPosition(controller.gain)}
            getAriaValueText={position => gainAriaValueText(positionToGain(position))}
            step={0.01}
            min={0.0}
            max={1.0}
            onChange={handleVolume}
            onChangeCommitted={onClose ? () => onClose() : undefined}
          />
          <Meter controller={controller} at="pre" />
          {controller.trackType === 'bus' ? <Meter controller={controller} at="post" color="primary" /> : null}
        </Grid>
        <Grid size={{ xs: 1 }}>
          <VolumeUp onClick={handleMax} />
        </Grid>
      </Grid>
    </div>
  )
}

export default Volume
